import { clsx } from 'clsx';
import type { ReactNode } from 'react';

/**
 * Chip — the kit's label pill (`maie.css` .chip / .chip-*).
 *
 * A STATUS chip is a label and never a control (deskkit §13.1). That rule is
 * enforced by the type: pass `onClick` and you get the `.chip-act` treatment,
 * which carries a 44px hit box behind its 32px visual; omit it and the chip
 * renders as a plain `<span>` that cannot be tabbed to or clicked.
 *
 * For an in-cell ACTION on the desk, use DChip instead — different size rules.
 */

export type ChipKind =
  | 'outline'
  | 'brand'
  | 'soft'
  | 'success'
  | 'warn'
  | 'danger'
  | 'info'
  | 'pink'
  | 'beige'
  | 'maie';

const KIND_CLASS: Record<ChipKind, string> = {
  outline: 'chip-outline',
  brand: 'chip-brand',
  soft: 'chip-soft',
  success: 'chip-success',
  warn: 'chip-warn',
  danger: 'chip-danger',
  info: 'chip-info',
  pink: 'chip-pink',
  beige: 'chip-beige',
  maie: 'chip-maie',
};

interface ChipProps {
  children: ReactNode;
  kind?: ChipKind;
  /** `.chip-sm` — the smaller label size. deskkit.statechip always uses it. */
  small?: boolean;
  /**
   * Makes the chip a control (`.chip-act`). Leaving this off is what keeps a
   * status chip a label — see the note above.
   */
  onClick?: () => void;
  /** Accessible name when the visible label is an icon or a bare count. */
  label?: string;
  /**
   * Hover text for a chip whose label is deliberately shorter than the fact
   * behind it — a safety chip reading `Penicillin` whose entry is `Penicillin
   * (urticarial rash, moderate)`. The full text stays reachable; the column
   * stays readable.
   */
  title?: string;
  className?: string;
}

export function Chip({
  children,
  kind = 'outline',
  small = false,
  onClick,
  label,
  title,
  className,
}: ChipProps) {
  const classes = clsx(
    'chip',
    KIND_CLASS[kind],
    small && 'chip-sm',
    onClick && 'chip-act',
    className,
  );

  if (!onClick) {
    return (
      <span className={classes} aria-label={label} title={title}>
        {children}
      </span>
    );
  }

  return (
    <button type="button" className={classes} onClick={onClick} aria-label={label} title={title}>
      {children}
    </button>
  );
}

/**
 * StateChip — deskkit.statechip. A status label, fixed small, never clickable.
 *
 * Exists as its own export because the design treats "this row is in state X"
 * as a distinct thing from a chip you can press, and keeping them separate
 * stops a status from quietly acquiring a click handler later.
 */
export function StateChip({
  children,
  kind = 'outline',
  className,
}: {
  children: ReactNode;
  /** deskkit.statechip offers a narrower set than the full chip palette. */
  kind?: Extract<ChipKind, 'outline' | 'warn' | 'success' | 'danger' | 'soft' | 'brand'>;
  className?: string;
}) {
  return (
    <Chip kind={kind} small className={className}>
      {children}
    </Chip>
  );
}

/**
 * ChipRow — the horizontally scrolling chip strip (`.chiprow`).
 *
 * The kit masks the right edge to signal overflow rather than showing a
 * scrollbar. Inside a grid artboard it wraps instead; that variant is driven by
 * a `.gridframe` ancestor in kit.css, so nothing is needed here.
 */
export function ChipRow({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={clsx('chiprow', className)}>{children}</div>;
}
